/**
 * The chart palette, as literal utility classes — ARCHITECTURE §16.7.
 *
 * These maps used to live inside `SimStatusChart`. They moved out the moment a
 * second surface (the stat band's share rails, the composition strip, the
 * active-share ring) needed the same five tones: two copies of "attention is
 * amber" is two chances for the dashboard to disagree with itself about what a
 * suspended SIM looks like, on one screen, side by side.
 *
 * **Every entry is a full literal class string, and never an interpolation.**
 * `` `fill-chart-${tone}` `` is the single most likely way this work ships
 * broken: Tailwind 4 discovers classes by scanning source *text*, so a name
 * assembled at runtime is never generated, and the mark renders unpainted —
 * black fill on a chart that looked correct in review. The same class of silent
 * failure as the tree-shaking bug recorded in `globals.css`.
 *
 * §16.7 ruling A governs the whole file: **a mark never follows the tenant.**
 * There is deliberately no `primary`/`accent` tone here to reach for.
 */

import {
  Ban,
  CircleCheck,
  CircleDashed,
  LoaderCircle,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';

/** The mark colours, as utility classes onto the §16.7 tokens. */
export type ChartTone = 'active' | 'progress' | 'attention' | 'stopped' | 'idle';

/** SVG marks — the strip's segments. */
export const TONE_FILL: Record<ChartTone, string> = {
  active: 'fill-chart-active',
  progress: 'fill-chart-progress',
  attention: 'fill-chart-attention',
  stopped: 'fill-chart-stopped',
  idle: 'fill-chart-idle',
};

/** HTML marks — a bar's fill, an activity dot. */
export const TONE_BG: Record<ChartTone, string> = {
  active: 'bg-chart-active',
  progress: 'bg-chart-progress',
  attention: 'bg-chart-attention',
  stopped: 'bg-chart-stopped',
  idle: 'bg-chart-idle',
};

/** SVG strokes — the ring's arc. */
export const TONE_STROKE: Record<ChartTone, string> = {
  active: 'stroke-chart-active',
  progress: 'stroke-chart-progress',
  attention: 'stroke-chart-attention',
  stopped: 'stroke-chart-stopped',
  idle: 'stroke-chart-idle',
};

/** The glyph beside a label, which is what keeps hue from being the only channel. */
export const TONE_TEXT: Record<ChartTone, string> = {
  active: 'text-chart-active',
  progress: 'text-chart-progress',
  attention: 'text-chart-attention',
  stopped: 'text-chart-stopped',
  idle: 'text-chart-idle',
};

export const TONE_ICON: Record<ChartTone, LucideIcon> = {
  active: CircleCheck,
  progress: LoaderCircle,
  attention: TriangleAlert,
  stopped: Ban,
  idle: CircleDashed,
};
